import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { PreOrdersComponent } from "../pre-orders/pre-orders.component";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Observable, map, startWith } from "rxjs";
import { TruckOwnerService } from "../truck-owner.service";
import { Router } from "@angular/router";

interface TruckOption {
  _id: string;
  truck_number: string;
}

@Component({
  selector: "app-pre-order-add-truck",
  templateUrl: "./pre-order-add-truck.component.html",
  styleUrls: ["./pre-order-add-truck.component.css"],
})
export class PreOrderAddTruckComponent implements OnInit {
  title: string = "";
  preOrderForm: FormGroup;
  lastValidValue = ""; // To store the last valid numeric value
  token: any;

  filteredOptions: Observable<TruckOption[]>; // Assuming this is your observable
  truckControl: FormControl;
  selectedTruck: any;
  selectedData: any;
  isLoading = false;

  constructor(
    private matDialogRef: MatDialogRef<PreOrdersComponent>,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private service: TruckOwnerService,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.title = data.data === "add" ? "Add Truck" : "Edit Truck";
    this.preOrderForm = this.fb.group({
      truck_number: this.truckControl, // Use the truckControl for validation
      truck_category: ["", Validators.required],
      truck_type: ["", Validators.required],
      driver_number: ["", Validators.required],
      price: ["", [Validators.required]],
    });
    if (data.data === 'add' && data.price) {
      this.preOrderForm.patchValue({
        price: data.price
      });
    }
    // Initialize truckControl with required validator
    this.truckControl = new FormControl("", Validators.required);
    if (data.item) {
      this.preOrderForm.patchValue({
        truck_number: data.item.truckInfo[0]?.truck_number,
        truck_category: data.item.truckCategoryInfo[0]?.truck_category_name,
        truck_type: data.item.truckTypeInfo[0]?.truck_weight,
        driver_number: data.item.driverInfo[0]?.mobile,
        price: data.item?.price,
      });
    }
  }

  ngOnInit(): void {
    this.token = sessionStorage.getItem("to_token");

    this.filteredOptions = this.truckControl.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter(value || ""))
    );
  }

  private _filter(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.data.selected
      .map((option: any) => option) // Include full option object for selected value
      .filter((option: any) =>
        option.truck_number.toLowerCase().includes(filterValue)
      );
  }

  // Handle option selection and log selected truck ID
  onOptionSelected(event: any): void {
    const selectedTruckNumber = event.option.value;
    this.selectedTruck = this.data.selected.find(
      (truck: any) => truck.truck_number === selectedTruckNumber
    );
    // Set the truck ID into the form control (truck_number will now hold the truck's ID)
    if (this.selectedTruck) {
      this.preOrderForm.patchValue({
        truck_number: this.selectedTruck._id, // Set the truck's _id in the truck_number field
      });
    }
    if (this.selectedTruck._id != "") {
      this.service
        .GetPreorderTruckType(this.selectedTruck?._id, this.token)
        .subscribe((response: any) => {
          this.selectedData = response.body.data;
          this.preOrderForm.patchValue({
            truck_category:
              this.selectedData[0]?.truckCategoryInfo[0]?.truck_category_name,
            truck_type: this.selectedData[0]?.truckTypeInfo[0]?.truck_weight,
            driver_number: this.selectedData[0]?.driverInfo[0]?.mobile,
          });
        });
    }
  }
  // Method to restrict input to numbers and two decimal places in real-time
  limitDecimalPlaces(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value;

    // Regular expression to allow numbers with up to two decimal places, no alphabetic characters
    const regex = /^\d*\.?\d{0,2}$/;

    // If the input matches the format, update last valid value
    if (regex.test(value)) {
      this.lastValidValue = value; // Update last valid value if input is valid
    } else {
      // Revert to the last valid value if input is invalid (contains letters or too many decimals)
      inputElement.value = this.lastValidValue;
      this.preOrderForm
        .get("price")
        ?.setValue(this.lastValidValue, { emitEvent: false });
    }
  }
  close() {
    this.matDialogRef.close();
  }

  // Convenience getter for easy access to form controls in the template
  get price() {
    return this.preOrderForm.get("price");
  }

  addTruck() {
    if (this.preOrderForm.valid) {
      const price = Number(this.preOrderForm.value.price);

      // Check if price is less than 1
      if (price < 1) {
        this.snackBar.open("Price/MT should not be less than 1", "Fail", {
          duration: 5000,
        });
        return; // Stop execution
      }
      this.isLoading = true; // Start the loader

      const data = {
        truck_id: this.preOrderForm.value.truck_number,
        price: Number(this.preOrderForm.value.price),
        truck_category_id: this.selectedData[0]?.truckCategoryInfo[0]?._id,
        truck_type_id: this.selectedData[0]?.truckTypeInfo[0]?._id,
        driver_id: this.selectedData[0]?.driverInfo[0]?._id,
        workorder_id: this.data.workorder_id._id,
      };
      this.service.addPreOrderTruck(data, this.token).subscribe(
        (response: any) => {
          this.isLoading = false; // Stop the loader

          this.matDialogRef.close({ event: "Success" });
        },
        (error) => {
          this.isLoading = false; // Stop the loader in case of error

          if (error.status == 404) {
            return;
          }

          if (error.error.message === "jwt malformed") {
            this.router.navigateByUrl("/");
            sessionStorage.clear();
          }
          if (error.error.message === "jwt expired") {
            this.router.navigateByUrl("/");
            sessionStorage.clear();
          }
          this.snackBar.open(error.error.message, "Fail", {
            duration: 5000,
          });
        }
      );
    }
  }

  updateTruck() {
    if (this.preOrderForm.valid) {
      const price = Number(this.preOrderForm.value.price);

      // Check if price is less than 1
      if (price < 1) {
        this.snackBar.open("Price/MT should not be less than 1", "Fail", {
          duration: 5000,
        });
        return; // Stop execution
      }
      this.isLoading = true; // Start the loader

      const data = {
        truck_id: this.data.item.truckInfo[0]?._id,
        price: Number(this.preOrderForm.value?.price),
        truck_category_id: this.data.item.truckCategoryInfo[0]?._id,
        truck_type_id: this.data.item.truckTypeInfo[0]?._id,
        driver_id: this.data.item.driverInfo[0]?._id,
      };
      this.service
        .updatePreOrderTruck(data, this.token, this.data.item._id)
        .subscribe(
          (response: any) => {
            this.isLoading = false; // Stop the loader
            this.matDialogRef.close({ event: "Success" });
          },
          (error) => {
            this.isLoading = false; // Stop the loader
            if (error.status == 404) {
              return;
            }

            if (error.error.message === "jwt malformed") {
              this.router.navigateByUrl("/");
              sessionStorage.clear();
            }
            if (error.error.message === "jwt expired") {
              this.router.navigateByUrl("/");
              sessionStorage.clear();
            }
            this.snackBar.open(error.error.message, "Fail", {
              duration: 5000,
            });
          }
        );
    }
  }
}
